import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError, auth } from "../../lib/api";
import { AuthLayout, Field, FormError, PrimaryButton, inputClass } from "./AuthLayout";

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await auth.forgot(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      // Spec §6 demands always-200 from the route handler. Anything else is
      // either a real transport hiccup or the rate-limit plugin firing before
      // the route runs (3/h per IP, see api/src/routes/auth.ts). Differentiate
      // so a busy day doesn't get diagnosed as "API down" (issue #100).
      if (err instanceof ApiError && err.kind === "rate_limited") {
        setError("Zu oft probiert. Bitte etwa eine Stunde warten und nochmal versuchen.");
      } else {
        setError("Keine Verbindung. Bitte später erneut versuchen.");
      }
    } finally {
      setPending(false);
    }
  }

  if (submitted) {
    return (
      <AuthLayout title="E-Mail unterwegs" subtitle="Falls ein Konto existiert.">
        <p className="text-sm text-fg-muted" data-testid="forgot-submitted">
          Wir haben einen Reset-Link an <strong className="text-fg">{email}</strong> geschickt, falls
          dieses Konto bei uns existiert. Schau in dein Postfach (auch Spam). Der Link ist 1 Stunde
          gültig.
        </p>
        <Link
          to="/login"
          className="block text-center min-h-[44px] py-2 rounded-lg text-accent hover:underline"
        >
          ← Zurück zum Login
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Passwort vergessen" subtitle="Wir schicken dir einen Reset-Link.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
        <Field label="E-Mail" htmlFor="email">
          <input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            className={inputClass}
            data-testid="email-input"
          />
        </Field>
        {error && <FormError message={error} />}
        <PrimaryButton pending={pending} testid="submit-forgot">
          Reset-Link anfordern
        </PrimaryButton>
        <div className="text-sm text-center pt-1">
          <Link to="/login" className="text-accent hover:underline">
            ← Zurück zum Login
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
